// LIBRARIES //
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import camelCase from 'lodash/camelCase';

// COMPONENTS //
import RaisedButton from 'material-ui/RaisedButton';
import Form from 'components/ui/Form';
import SearchableSelect from 'components/shared/SearchableSelect';
import Header from 'components/shared/Header';
import CtaText from 'components/ui/CtaCard';
import Loading from 'components/ui/Animations/Loading';

// HELPER //
import { AccentColor } from 'helpers/AccentColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { toFriendlyDateString } from 'helpers/Formatter';
import { colors } from 'helpers/ColorHelper';
import { media } from 'helpers/MediaHelper';

// ACTIONS //
import {
  runSpecificCreditCheck,
  getCreditReportStatus,
  getApplication,
} from 'actions/landlordApplicationsActions';

// ICONS //
import ErrorIcon from 'assets/CloseCircle.png';
import HelperIcon from 'assets/ExclamationCircle.png';

const StyledPulledCreditCheck = styled.div`
  display: flex;
  background-color: ${colors.LIGHT_GREY};
  padding: 16px 24px;
  border-radius: 8px;
  justify-content: space-between;
  max-height: 53px;
  ${media.mobile`
        flex-direction: column;
        max-height: unset;
    `};
`;

const StyledHelperParentContainer = styled.div`
  display: flex;
  background-color: ${(props) => props.backgroundColor};
  padding: 16px 24px;
  justify-content: space-between;
  border-radius: 4px;
`;

const StyledHelperTextContainer = styled.div`
  margin-left: 15px;
`;

const StyledHelperTextHeader = styled.div`
  font-size: 16px;
  margin-bottom: 4px;
`;

const GreyText = styled.div`
  margin-left: 16px;
  color: ${colors.ICON_GRAY};
  font-style: italic;
  ${media.mobile`
        margin-left: 0px;
    `};
`;

const StyledSectionHeader = styled.h5`
  margin: 24px 0px 8px;
`;

const StyledApplicantContainer = styled.div`
  display: flex;
  align-items: center;
  ${media.mobile`
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 8px;
    `};
`;

const ModifiedIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.ERROR_RED};
  margin-right: 8px;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled.img`
  width: 21px;
  height: 21px;
  margin-top: 4px;
`;

const StyledPulledReportList = styled.div`
  margin-bottom: 24px;
`;

const CreditCheckValidationSchema = Yup.object().shape({
  applicant_lead_id: Yup.string().required(getTranslatedString('required')),
  credit_check_type: Yup.string().required(getTranslatedString('required')),
});

const CreditCheckOptions = ({
  application,
  runSpecificCreditCheck,
  getCreditReportStatus,
  getApplication,
  certnCreditReport,
  certnSoftcheck,
  certnUSCriminalCheck,
  currentLanguage,
}) => {
  const [selectableCotenants, setSelectableCotenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [creditCheckSelection, setCeditCheckSelection] = useState([]);

  useEffect(() => {
    if (application && application.cotenants) {
      const list = application.cotenants.map((cotenant) => ({
        label: cotenant.full_name,
        value: cotenant.lead_id,
      }));
      setSelectableCotenants(list);
    }
    const propertyCreditCheckOverride = application.property_credit_check_permissions;
    let creditReportOptions = [];
    if (certnCreditReport) {
      creditReportOptions.push({
        label: 'Credit Check',
        value: 'credit_check',
      });
    }
    if (
      certnCreditReport &&
      certnSoftcheck &&
      ['company_defaults', 'credit_soft_check'].includes(propertyCreditCheckOverride)
    ) {
      creditReportOptions.push({
        label: 'Credit Soft Check',
        value: 'softcheck_credit_report',
      });
    }
    if (
      certnCreditReport &&
      certnUSCriminalCheck &&
      ['company_defaults', 'us_criminal_credit_check'].includes(propertyCreditCheckOverride)
    ) {
      creditReportOptions.push({
        label: 'US Criminal Credit Check',
        value: 'us_criminal_credit_report',
      });
    }
    if (
      certnCreditReport &&
      certnSoftcheck &&
      certnUSCriminalCheck &&
      ['company_defaults', 'credit_soft_check', 'us_criminal_credit_check'].includes(
        propertyCreditCheckOverride
      )
    ) {
      creditReportOptions.push({
        label: 'US Criminal Credit Soft Check',
        value: 'us_criminal_credit_softcheck',
      });
    }
    setCeditCheckSelection(creditReportOptions);
  }, [
    application,
    setSelectableCotenants,
    certnCreditReport,
    certnSoftcheck,
    certnUSCriminalCheck,
    setCeditCheckSelection,
  ]);

  if (!application.can_view_credit_check) return null;

  return (
    <div>
      <Header name={getTranslatedString('landlordPortal.applications.creditCheckOptions.header')} />
      <MuiStyledRow>
        <StyledSectionHeader>
          {getTranslatedString(
            'landlordPortal.applications.creditCheckOptions.pulledCreditSoftChecks'
          )}
        </StyledSectionHeader>
      </MuiStyledRow>
      {application.cotenants &&
        application.cotenants.map((cotenant) => (
          <PulledCreditCheckList
            cotenant={cotenant}
            getCreditReportStatus={getCreditReportStatus}
            applicationId={application.id}
            getApplication={getApplication}
            currentLanguage={currentLanguage}
          />
        ))}
      <MuiStyledRow>
        <p>
          {getTranslatedString('landlordPortal.applications.creditCheckOptions.ifYouLiketoPerform')}
        </p>
        <p
          dangerouslySetInnerHTML={{
            __html: getTranslatedString(
              'landlordPortal.applications.creditCheckOptions.creditReportDescription'
            ),
          }}
        />
      </MuiStyledRow>
      <Formik
        initialValues={{
          applicant_lead_id: undefined,
          credit_check_type: undefined,
        }}
        validationSchema={CreditCheckValidationSchema}
        onSubmit={async (values) => {
          setIsLoading(true);
          await runSpecificCreditCheck(values.applicant_lead_id, values.credit_check_type);
          await getApplication(application.id);
          setIsLoading(false);
        }}
      >
        {({ values, setFieldValue, submitForm, errors, touched }) => (
          <form>
            <MuiStyledRow>
              <Form.Item
                validateStatus={errors.applicant_lead_id && 'error'}
                help={errors.applicant_lead_id}
              >
                <SearchableSelect
                  name="applicant_lead_id"
                  data={selectableCotenants}
                  label={getTranslatedString(
                    'landlordPortal.applications.creditCheckOptions.selectApplicant'
                  )}
                  fullWidth
                  onChange={(selectedApplicant) =>
                    setFieldValue('applicant_lead_id', selectedApplicant.value)
                  }
                />
              </Form.Item>
            </MuiStyledRow>
            <MuiStyledRow>
              <Form.Item
                validateStatus={errors.credit_check_type && 'error'}
                help={errors.credit_check_type}
              >
                <SearchableSelect
                  name="credit_check_type"
                  data={creditCheckSelection}
                  label={getTranslatedString(
                    'landlordPortal.applications.creditCheckOptions.whatTypeOfCheck'
                  )}
                  fullWidth
                  onChange={(selectedCreditCheck) =>
                    setFieldValue('credit_check_type', selectedCreditCheck.value)
                  }
                />
              </Form.Item>
            </MuiStyledRow>
            <MuiStyledRow>
              <RaisedButton
                label={getTranslatedString(
                  'landlordPortal.applications.creditCheckOptions.performCheck'
                )}
                primary
                onClick={() => submitForm()}
                style={{
                  backgroundColor: AccentColor(),
                  color: 'white',
                  borderRadius: '8px',
                  marginTop: '10px',
                }}
                disabled={isLoading}
              />
            </MuiStyledRow>
          </form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = (store) => ({
  certnCreditReport: store.companyDetails.companyPreferences.certn_credit_report,
  certnSoftcheck: store.companyDetails.companyPreferences.certn_softcheck,
  certnUSCriminalCheck: store.companyDetails.companyPreferences.certn_softcheck_us,
  currentLanguage: store.locale.currentLanguage,
});

const mapDispatchToProps = {
  runSpecificCreditCheck,
  getCreditReportStatus,
  getApplication,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCheckOptions);

const MuiStyledRow = ({ children }) => (
  <div className="row" style={{ margin: '0px 0 8px' }}>
    <div className="col-xs-12">{children}</div>
  </div>
);

const PulledCreditCheckList = ({
  cotenant,
  getCreditReportStatus,
  applicationId,
  getApplication,
  currentLanguage,
}) => {
  const [fetchingCreditCheck, setFetchingCreditCheck] = useState(false);
  const creditCheckUrl = cotenant.general_details.credit_check_url;
  const creditCheckIsAnalyzing = cotenant.general_details.credit_check_is_analyzing;
  const creditCheckType = cotenant.general_details.credit_check_type;
  const creditCheckError = cotenant.general_details.credit_check_error;
  let helperVerbiage = '';

  if (isEmpty(creditCheckUrl) && !creditCheckIsAnalyzing && isEmpty(creditCheckError)) return null;

  let ctaVerbiage = getTranslatedString(
    'landlordPortal.applications.creditCheckOptions.view.creditCheck'
  );

  if (!isEmpty(creditCheckUrl) && !isEmpty(creditCheckType)) {
    ctaVerbiage = getTranslatedString(
      `landlordPortal.applications.creditCheckOptions.view.${camelCase(creditCheckType)}`
    );
  }

  if (creditCheckIsAnalyzing && isEmpty(creditCheckUrl)) {
    ctaVerbiage = getTranslatedString(
      'landlordPortal.applications.creditCheckOptions.retrieveCreditCheck'
    );
    helperVerbiage = getTranslatedString(
      'landlordPortal.applications.creditCheckOptions.clickToRetrive'
    );
  }

  if (creditCheckError) {
    ctaVerbiage = 'Error Processing Check';
    helperVerbiage = getTranslatedString(
      `landlordPortal.applications.creditCheckOptions.helperVerbiage.${camelCase(creditCheckError)}`
    );
  }

  const handleClick = async () => {
    if (creditCheckIsAnalyzing && isEmpty(creditCheckUrl)) {
      setFetchingCreditCheck(true);
      await getCreditReportStatus(cotenant.lead_id);
      await getApplication(applicationId);
      setFetchingCreditCheck(false);
      return;
    } else {
      window.open(creditCheckUrl, '_blank');
    }
  };

  return (
    <StyledPulledReportList>
      <MuiStyledRow>
        <StyledPulledCreditCheck>
          <StyledApplicantContainer>
            <div>{cotenant.full_name}</div>
            <div>
              {cotenant.general_details.credit_check_updated_at && (
                <GreyText>
                  {getTranslatedString('landlordPortal.applications.creditCheckOptions.pulledOn', {
                    date: toFriendlyDateString(cotenant.general_details.credit_check_updated_at, {
                      language: currentLanguage,
                    }),
                  })}
                </GreyText>
              )}
            </div>
          </StyledApplicantContainer>
          <div>
            {fetchingCreditCheck ? (
              <Loading height={30} width={30} style={{ margin: '0px' }} />
            ) : creditCheckError ? (
              <StyledErrorMessageContainer>
                <ModifiedIndicator />
                {getTranslatedString(
                  'landlordPortal.applications.creditCheckOptions.errorVerbiage'
                )}
              </StyledErrorMessageContainer>
            ) : (
              <CtaText style={{ padding: 0 }} onClick={handleClick} disabled={fetchingCreditCheck}>
                {ctaVerbiage}
              </CtaText>
            )}
          </div>
        </StyledPulledCreditCheck>
      </MuiStyledRow>

      {creditCheckError && (
        <MuiStyledRow>
          <HelperVerbiageSection
            backgroundColor={colors.HELPER_RED}
            helperIcon={ErrorIcon}
            headerVerbiage={getTranslatedString(
              'landlordPortal.applications.creditCheckOptions.errorProcessingCheck'
            )}
            helperVerbiage={helperVerbiage}
          />
        </MuiStyledRow>
      )}
      {creditCheckIsAnalyzing && isEmpty(creditCheckUrl) && (
        <MuiStyledRow>
          <HelperVerbiageSection
            backgroundColor={colors.HELPER_ORANGE}
            helperIcon={HelperIcon}
            headerVerbiage={getTranslatedString(
              'landlordPortal.applications.creditCheckOptions.requestInProgress'
            )}
            helperVerbiage={helperVerbiage}
          />
        </MuiStyledRow>
      )}
    </StyledPulledReportList>
  );
};

const HelperVerbiageSection = ({ backgroundColor, helperIcon, headerVerbiage, helperVerbiage }) => {
  return (
    <StyledHelperParentContainer backgroundColor={backgroundColor}>
      <div>
        <StyledIcon src={helperIcon} alt="" />
      </div>
      <StyledHelperTextContainer>
        <StyledHelperTextHeader>{headerVerbiage}</StyledHelperTextHeader>
        <div>{helperVerbiage}</div>
      </StyledHelperTextContainer>
    </StyledHelperParentContainer>
  );
};
